import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Modal, Select, TextArea } from './Common';

const STEPS = ['Client & Type', 'Scope', 'Dates & Contacts', 'Template & Review'];
const TEST_TYPES = ['Web Application', 'API', 'Mobile Application', 'Network', 'PCI DSS', 'Wireless', 'Social Engineering', 'Physical', 'Other'];

export default function SowWizard({ isOpen, onClose, onGenerated }) {
  const { apiFetch } = useAuth();
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [applications, setApplications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    clientId: '',
    sowType: 'scoped',
    sowNumber: '',
    purchaseOrder: '',
    // Scoped
    selectedApps: [],
    pricePerUnit: '',
    // Bulk
    creditCount: '',
    creditDuration: '5 business days',
    services: { webApp: true, api: true, mobile: false, network: false, pci: false },
    // Dates & contacts
    startDate: '',
    endDate: '',
    providerContact: '',
    providerEmail: '',
    providerPhone: '',
    clientContact: '',
    clientEmail: '',
    clientPhone: '',
    // Template
    templateId: '',
    notes: '',
  });

  const up = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        apiFetch('/clients'),
        apiFetch('/sow-templates'),
      ]);
      if (cRes.ok) setClients(await cRes.json());
      if (tRes.ok) setTemplates(await tRes.json());
    } catch {}
  }, [apiFetch]);

  useEffect(() => { if (isOpen) { loadData(); setStep(0); setError(null); } }, [isOpen, loadData]);

  // Load applications when client changes
  useEffect(() => {
    if (!form.clientId) { setApplications([]); return; }
    (async () => {
      try {
        const res = await apiFetch(`/applications?clientId=${form.clientId}`);
        if (res.ok) setApplications(await res.json());
      } catch {}
    })();
  }, [form.clientId, apiFetch]);

  const toggleApp = (app) => {
    setForm(f => {
      const exists = f.selectedApps.find(a => a.id === app.id);
      if (exists) return { ...f, selectedApps: f.selectedApps.filter(a => a.id !== app.id) };
      return { ...f, selectedApps: [...f.selectedApps, { id: app.id, name: app.name, testType: 'Web Application', notes: '', price: f.pricePerUnit || '' }] };
    });
  };

  const updateApp = (appId, key, val) => {
    setForm(f => ({
      ...f,
      selectedApps: f.selectedApps.map(a => a.id === appId ? { ...a, [key]: val } : a)
    }));
  };

  const totalPrice = form.sowType === 'scoped'
    ? form.selectedApps.reduce((sum, a) => sum + Number(a.price || form.pricePerUnit || 0), 0)
    : (Number(form.creditCount) || 0) * (Number(form.pricePerUnit) || 0);

  const canProceed = () => {
    if (step === 0) return form.clientId && form.sowNumber;
    if (step === 1) {
      if (form.sowType === 'scoped') return form.selectedApps.length > 0;
      return Number(form.creditCount) > 0 && Number(form.pricePerUnit) > 0;
    }
    if (step === 2) return true;
    return true;
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const body = {
        clientId: form.clientId,
        sowType: form.sowType,
        sowNumber: form.sowNumber,
        purchaseOrder: form.purchaseOrder,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        providerContact: form.providerContact,
        providerEmail: form.providerEmail,
        providerPhone: form.providerPhone,
        clientContact: form.clientContact,
        clientEmail: form.clientEmail,
        clientPhone: form.clientPhone,
        templateId: form.templateId || null,
        notes: form.notes,
        pricePerUnit: form.pricePerUnit,
      };
      if (form.sowType === 'scoped') {
        body.applications = form.selectedApps;
      } else {
        body.creditCount = Number(form.creditCount);
        body.creditDuration = form.creditDuration;
      }

      const res = await apiFetch('/sow-generator/generate', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      onGenerated(data);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const selectedClient = clients.find(c => c.id === form.clientId);

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg-input)',
    color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Generate Statement of Work" width={640} noOverlayClose>
      {/* Step Indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: 4, borderRadius: 2, marginBottom: 4,
              background: i <= step ? 'var(--primary)' : 'var(--border)',
              transition: 'background 0.2s',
            }} />
            <span style={{ fontSize: 10, color: i <= step ? 'var(--primary)' : 'var(--text-muted)', fontWeight: i === step ? 700 : 400 }}>
              {s}
            </span>
          </div>
        ))}
      </div>

      {/* Step 1: Client & Type */}
      {step === 0 && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Client *</label>
            <select value={form.clientId} onChange={e => up('clientId', e.target.value)} style={inputStyle}>
              <option value="">Select client...</option>
              {clients.filter(c => c.is_active).sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>SoW Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ v: 'scoped', l: 'Scoped (Per Application)' }, { v: 'bulk', l: 'Bulk (Credit-Based)' }].map(t => (
                <button key={t.v} onClick={() => up('sowType', t.v)}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 'var(--radius)',
                    border: `2px solid ${form.sowType === t.v ? 'var(--primary)' : 'var(--border)'}`,
                    background: form.sowType === t.v ? 'rgba(59,130,246,0.08)' : 'var(--bg-input)',
                    color: form.sowType === t.v ? 'var(--primary)' : 'var(--text)',
                    cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  }}>
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>SoW Number *</label>
              <input value={form.sowNumber} onChange={e => up('sowNumber', e.target.value)}
                placeholder="e.g. SOW-2026-001" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Purchase Order</label>
              <input value={form.purchaseOrder} onChange={e => up('purchaseOrder', e.target.value)}
                placeholder="Optional" style={inputStyle} />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Scope */}
      {step === 1 && form.sowType === 'scoped' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Default Price Per Engagement ($)</label>
            <input type="number" min="0" step="0.01" value={form.pricePerUnit}
              onChange={e => up('pricePerUnit', e.target.value)} placeholder="e.g. 7500" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Select Applications</label>
            {applications.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', padding: 8 }}>
                No applications found for this client. Create applications first in the Applications page.
              </div>
            ) : (
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                {applications.map(app => {
                  const selected = form.selectedApps.find(a => a.id === app.id);
                  return (
                    <label key={app.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderBottom: '1px solid var(--border)', cursor: 'pointer',
                      background: selected ? 'rgba(59,130,246,0.06)' : 'transparent',
                    }}>
                      <input type="checkbox" checked={!!selected} onChange={() => toggleApp(app)} style={{ cursor: 'pointer' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{app.name}</div>
                        {app.url && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{app.url}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          {form.selectedApps.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Selected Applications ({form.selectedApps.length})</label>
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                {form.selectedApps.map(app => (
                  <div key={app.id} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 120px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{app.name}</span>
                    <select value={app.testType} onChange={e => updateApp(app.id, 'testType', e.target.value)} style={{ ...inputStyle, fontSize: 11, padding: '4px 6px' }}>
                      {TEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="number" value={app.price || form.pricePerUnit || ''} onChange={e => updateApp(app.id, 'price', e.target.value)}
                      placeholder="Price" style={{ ...inputStyle, fontSize: 11, padding: '4px 6px' }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {step === 1 && form.sowType === 'bulk' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Number of Credits *</label>
              <input type="number" min="1" step="1" value={form.creditCount}
                onChange={e => up('creditCount', e.target.value)} placeholder="e.g. 20" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Price Per Credit ($) *</label>
              <input type="number" min="0" step="0.01" value={form.pricePerUnit}
                onChange={e => up('pricePerUnit', e.target.value)} placeholder="e.g. 8000" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Duration Per Credit</label>
            <input value={form.creditDuration} onChange={e => up('creditDuration', e.target.value)}
              placeholder="e.g. 5 business days" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Service Types Included</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {[
                { key: 'webApp', label: 'Web Application' },
                { key: 'api', label: 'API' },
                { key: 'mobile', label: 'Mobile' },
                { key: 'network', label: 'Network' },
                { key: 'pci', label: 'PCI DSS' },
              ].map(s => (
                <label key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.services[s.key] || false}
                    onChange={e => up('services', { ...form.services, [s.key]: e.target.checked })} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Dates & Contacts */}
      {step === 2 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Start Date</label>
              <input type="date" value={form.startDate} onChange={e => up('startDate', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>End Date</label>
              <input type="date" value={form.endDate} onChange={e => up('endDate', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>TSR Representative</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
            <div><label style={labelStyle}>Name</label><input value={form.providerContact} onChange={e => up('providerContact', e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>Email</label><input value={form.providerEmail} onChange={e => up('providerEmail', e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>Phone</label><input value={form.providerPhone} onChange={e => up('providerPhone', e.target.value)} style={inputStyle} /></div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Client Representative
            {selectedClient?.contact_name && (
              <button onClick={() => setForm(f => ({
                ...f,
                clientContact: selectedClient.contact_name || '',
                clientEmail: selectedClient.contact_email || '',
                clientPhone: selectedClient.contact_phone || '',
              }))}
                style={{ marginLeft: 8, background: 'none', border: 'none', fontSize: 11, color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit' }}>
                Auto-fill from client
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div><label style={labelStyle}>Name</label><input value={form.clientContact} onChange={e => up('clientContact', e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>Email</label><input value={form.clientEmail} onChange={e => up('clientEmail', e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>Phone</label><input value={form.clientPhone} onChange={e => up('clientPhone', e.target.value)} style={inputStyle} /></div>
          </div>
        </div>
      )}

      {/* Step 4: Template & Review */}
      {step === 3 && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>SoW Template</label>
            <select value={form.templateId} onChange={e => up('templateId', e.target.value)} style={inputStyle}>
              <option value="">Default Template</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (Default)' : ''}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => up('notes', e.target.value)} rows={2}
              placeholder="Optional internal notes..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          {/* Review Summary */}
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            padding: 16, fontSize: 12,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>Review</div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '4px 12px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Client:</span>
              <span style={{ fontWeight: 600 }}>{selectedClient?.name || '-'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>Type:</span>
              <span>{form.sowType === 'scoped' ? 'Scoped (Per Application)' : 'Bulk (Credit-Based)'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>SoW Number:</span>
              <span>{form.sowNumber}</span>
              {form.purchaseOrder && <>
                <span style={{ color: 'var(--text-secondary)' }}>PO:</span>
                <span>{form.purchaseOrder}</span>
              </>}
              {form.sowType === 'scoped' ? <>
                <span style={{ color: 'var(--text-secondary)' }}>Applications:</span>
                <span>{form.selectedApps.length}</span>
              </> : <>
                <span style={{ color: 'var(--text-secondary)' }}>Credits:</span>
                <span>{form.creditCount} x ${Number(form.pricePerUnit || 0).toLocaleString()}</span>
              </>}
              <span style={{ color: 'var(--text-secondary)' }}>Total:</span>
              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>${totalPrice.toLocaleString()}</span>
              {form.startDate && <>
                <span style={{ color: 'var(--text-secondary)' }}>Term:</span>
                <span>{form.startDate} to {form.endDate || 'TBD'}</span>
              </>}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 'var(--radius)', background: '#7f1d1d', color: '#fca5a5', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <div>
          {step > 0 && <Button variant="secondary" onClick={() => setStep(step - 1)}>Back</Button>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>Next</Button>
          ) : (
            <Button onClick={generate} disabled={generating || !canProceed()}>
              {generating ? 'Generating...' : 'Generate SoW'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
